// ─────────────────────────────────────────────────────────────────────────────
// ComposerAgentControls — LOOP and CALL, as chips in the composer row.
//
// Owner ruling 2026-08-27, from the live capture: the two stacked 460px cards
// took the top-left quarter of the dashboard, pushed the chat down and sat OVER
// the surface rather than in it. Both controls move to the composer row, beside
// `+` and the LOCAL chip — where the owner already is, and the dashboard is free.
//
//   + · LOCAL · LOOP · CALL
//
// CHIP-SIZED IN THE ROW'S OWN IDIOM, NOT A CARD SHRUNK. That distinction is the
// fault this build already paid for once: VoiceEngineToggle's styles were right
// for a narrow card and wrong in a full-width row. The idiom belongs to the
// container, so these take the LOCAL chip's — the same rounded-full, 9px,
// uppercase, tracking-widest pill, with the row's inset-pressed depth.
//
// ── ChatSurface.tsx IS NOT TOUCHED, AND DID NOT NEED TO BE ─────────────────
// The composer row is `InputBox.tsx` — its own 888-line file that ChatSurface
// merely renders. InputBox already receives `activeAgent`; what it lacked was a
// DOMAIN, and that arrives through AppShellLiveContext, which AppShell fills
// from props it already holds. No prop was added to ChatSurface, so CI-6 and the
// Flag 1 invariant are untouched rather than worked around.
//
// ── A REFUSAL IS NEVER SHORTENED TO FIT A CHIP ─────────────────────────────
// A chip has less room than a card, and the refusals ARE the value of these
// controls — the capture proved they work. So the chip carries the STATE, and
// any refusal that cannot fit in a chip gets its own surface: a panel above the
// row, carrying the sentence in full. Nothing is truncated.
//
// ── BUT IT IS NOT OPEN BY DEFAULT (owner ruling 2026-08-31) ────────────────
// "IT SITS OPEN, PERMANENTLY, OVER THE CONVERSATION." It did: `details.length >
// 0` put a 380px panel over the chat and left it there, because on most agents
// at least one sentence always applies. THE SENTENCES ARE NOT THE PROBLEM AND
// NONE OF THEM CHANGED — the owner said so outright. The panel is now behind a
// NOTICES chip that lives in the row with the others:
//
//     + · LOCAL · RUN · ⓘ2 · LANE · CALL
//
// CLOSED, IT IS NOT IN THE DOM AT ALL. That is a stronger claim than "it is
// out of the way" and it is the one a bar can count: zero nodes, so zero pixels
// over the conversation, with no geometry argument required. Open, it is a
// deliberate act — click, Escape, click-away, or a change of agent closes it.
//
// THE COUNT IS THE PERSISTENT INDICATOR. The information stays reachable and
// stays advertised; what stops being permanent is the wall of text.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { AppShellLiveContext } from '../shell/AppShellContext';
import { resolveCallTarget } from '../../lib/callTarget';
import { getVoiceSet, voiceForAgent, VoiceSet } from '../../api/agentVoice';
import {
  getAgentTransport, setAgentTransport, AgentTransportState, TransportLane,
} from '../../api/agentTransport';
import { resolveTransport } from '../../lib/transportLane';
import { useAgentRun, RunChip } from './AgentRunControl';

const CHIP = 'px-2.5 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest '
  + 'border border-rexy-border transition-colors';

const PRESSED = 'inset 0 1px 2px rgba(0,0,0,0.55), 0 1px 0 rgba(255,255,255,0.06)';

export default function ComposerAgentControls() {
  const live = useContext(AppShellLiveContext);
  const domain = live?.navDomain || '';
  const agent = live?.navAgent || null;
  const name = (agent || '').toUpperCase();

  // ── THE RUN CONTROL IS NO LONGER THIS FILE'S STATE (owner ruling 2026-08-31) ──
  // It moved to `state/agentRunStore` so the dashboard and the domain rail render
  // THE SAME control off THE SAME answer. Three surfaces asking the box
  // separately would be three answers to "is this agent working", which is the
  // fault the ruling names. What is left here is the paint.
  const { view: runView, strip, press: pressRun } = useAgentRun(domain, agent);

  const [voiceSet, setVoiceSet] = useState<VoiceSet | null>(null);
  const [callErr, setCallErr] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  // THE TRANSPORT LANE. `null` is UNREAD, and that is a different fact from
  // "read, and it is local" — resolveTransport says which. No lane is ever held
  // here as a client-side selection: the setting is backend state keyed
  // "DOMAIN:agent", and a lane surviving only in React would look like a saved
  // setting to anyone who reloaded the page.
  const [transport, setTransport] = useState<AgentTransportState | null>(null);
  const [transportPending, setTransportPending] = useState(false);
  // CLOSED IS THE ONLY DEFAULT. Never seeded from whether notices exist — that
  // is precisely the rule that made it permanent, since on most agents at least
  // one sentence always applies.
  const [noticesOpen, setNoticesOpen] = useState(false);

  // ONE generation ref for both reads and both writes: a reply that arrives after
  // the mounted identity has moved is dropped, so BUCK can never paint on DELTA.
  const gen = useRef(0);
  const identity = `${domain.toUpperCase()}/${(agent || '').toLowerCase()}`;

  useEffect(() => {
    gen.current += 1;
    const mine = gen.current;
    setVoiceSet(null); setCallErr(null); setStarting(false);
    setTransport(null); setTransportPending(false);
    // A NEW AGENT CLOSES IT. The notices it holds are about the agent that was
    // open; carrying the panel across would leave BUCK's sentences hanging over
    // DELTA for the frame before they are replaced.
    setNoticesOpen(false);
    if (!agent || !domain) return;
    getVoiceSet()
      .then((v) => { if (gen.current === mine) setVoiceSet(v); })
      .catch(() => { if (gen.current === mine) setVoiceSet(null); });
    // The transport read resolves for EVERY outcome including 404 — an unread
    // route is a STATE this surface must render, not an exception to swallow.
    // The catch is belt-and-braces and leaves it UNREAD, never local.
    getAgentTransport(domain, agent)
      .then((t) => { if (gen.current === mine) setTransport(t); })
      .catch(() => { if (gen.current === mine) setTransport(null); });
  }, [identity, domain, agent]);

  // ── THE RUNNER POLL LIVES IN THE STORE NOW, AND IS REFCOUNTED ───────────
  // It used to be a 15s interval owned by this component. With the control on
  // three surfaces that would have been THREE intervals against a box the
  // control exists to unstarve — the disease, in miniature. `useAgentRun` holds
  // one shared interval between every mount and releases it with the last.
  //
  // The `stopped`-guard note that stood here is preserved at the store's own
  // read, which is the only place the late reply can now land.

  // ── DISMISSAL, AND BOTH WAYS OUT ARE WIRED ─────────────────────────────
  // Escape and a click anywhere else. The root div stops propagation on click
  // (it always has — the composer must not swallow the row's own presses), so
  // "outside" is genuinely outside and the listener cannot be tripped by a
  // press on the chips themselves.
  //
  // Bound only WHILE OPEN. A permanently-bound document listener for a panel
  // that is closed almost all the time is the same class of always-on as the
  // panel this replaces.
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!noticesOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setNoticesOpen(false); };
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setNoticesOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [noticesOpen]);

  // ONE DEFINITION. The chip renders from this and the click reads the same
  // object through `tvRef`, so the label and the guard can never disagree — the
  // rule `callTarget.assertCallAllowed` already holds for the call button.
  const tv = resolveTransport(transport, name);
  const tvRef = useRef(tv);
  tvRef.current = tv;

  const verdict = resolveCallTarget({
    domain, agent, boxCall: live?.boxCall ?? null, starting,
    adopted: live?.adopted ?? null,
    laneReady: live?.start ? undefined : false,
    error: live?.start ? null : 'this surface cannot place a per-agent call',
  });

  // SAME SHAPE AS THE RUN CONTROL, DELIBERATELY. The click does not move the
  // control; only what the backend returns does. A failed write RE-READS rather
  // than keeping the lane that was clicked, because a chip showing PHONE NUMBER
  // after a write that did not land is the exact claim this whole control was
  // built to avoid making.
  const toggleTransport = useCallback(() => {
    if (transportPending || !tvRef.current.armable || !tvRef.current.next) return;
    if (!agent) return;
    const next: TransportLane = tvRef.current.next;
    gen.current += 1;
    const mine = gen.current;
    setTransportPending(true);
    setAgentTransport(domain, agent, next)
      .then((t) => { if (gen.current === mine) { setTransport(t); setTransportPending(false); } })
      .catch(() => {
        getAgentTransport(domain, agent)
          .then((t) => { if (gen.current === mine) { setTransport(t); setTransportPending(false); } })
          .catch(() => { if (gen.current === mine) { setTransport(null); setTransportPending(false); } });
      });
  }, [transportPending, domain, agent]);

  const pressCall = useCallback(() => {
    if (!verdict.actionable || !live?.start || !agent) return;
    if (verdict.action === 'end') { live.ptt(); setStarting(false); return; }
    gen.current += 1;
    const mine = gen.current;
    setCallErr(null);
    setStarting(true);
    live.start(domain, agent)
      .catch((e: any) => { if (gen.current === mine) setCallErr(String(e?.message ?? e)); })
      .finally(() => { if (gen.current === mine) setStarting(false); });
  }, [verdict, live, domain, agent]);

  if (!agent || !domain) return null;

  const voice = voiceForAgent(agent, voiceSet);

  // Every refusal that will not fit in a chip, in full. Order is stable so a
  // gate can assert on it.
  const details: string[] = [];
  // THE RUN CONTROL'S OWN SENTENCE, composed once in `lib/runControl` and
  // rendered identically on the dashboard and the rail. It already folds in what
  // arming the harness agent actually does, so nothing is appended to it here.
  if (runView.detail) details.push(runView.detail);
  // In CHIP ORDER — the panel reads top-to-bottom as the row reads left-to-right.
  if (tv.detail) details.push(tv.detail);
  if (verdict.kind === 'busy_elsewhere') {
    details.push(`A CALL WITH ${verdict.otherAgent} IS IN PROGRESS. END IT BEFORE CALLING `
      + `${name} — STARTING ONE HERE WOULD REPLACE IT.`);
  } else if (verdict.kind === 'unavailable') {
    details.push((verdict.reason || 'THE VOICE LANE IS NOT REACHABLE FROM THIS SURFACE.').toUpperCase());
  } else if (!voice.known) {
    // NOT "this agent has no voice" — that is a claim about the agent, and it is
    // not the one being made. Nothing publishes the voice set: `/api/voice/voices`
    // does not exist, `/api/voice/availability` carries only {mode, local_service,
    // local_healthy}, and the list lives on the TTS service's own :8754 /health,
    // which a browser cannot reach. Naming the gap beats implying a defect in the
    // agent, and BOTH beat claiming a voice that has not been verified.
    details.push(`VOICE NOT PUBLISHED — NO ENDPOINT LISTS WHICH AGENTS HAVE A VOICE YET, `
      + `SO WHOSE VOICE ANSWERS CANNOT BE SHOWN. THE CALL STILL PLACES NORMALLY.`);
  } else if (voice.isPlaceholder) {
    details.push(`${name} HAS NO VOICE OF HER OWN YET — SHE WILL SPEAK WITH `
      + `${String(voice.voice).toUpperCase()}'S VOICE.`);
  }
  if (callErr) details.push(callErr.toUpperCase());

  const callLabel = verdict.kind === 'in_call' ? 'END'
    : verdict.kind === 'connecting' ? 'CONNECTING'
    : verdict.kind === 'busy_elsewhere' ? `BUSY · ${verdict.otherAgent}`
    : verdict.kind === 'unavailable' ? 'CALL —' : 'CALL';

  // WHAT THE INDICATOR COUNTS. The strip is a live reading rather than a notice,
  // so it opens the panel without being counted as one — a "1" beside a chat
  // where nothing is wrong would be a warning about nothing.
  const noticeCount = details.length;
  const hasPanel = noticeCount > 0 || !!strip;
  const panelOpen = noticesOpen && hasPanel;

  return (
    <div
      ref={rootRef}
      className="relative flex items-center gap-2"
      onClick={(e) => e.stopPropagation()}
    >
      {/* THE REFUSAL SURFACE — RENDERED ONLY WHEN ASKED FOR. Closed it is not in
          the DOM, so it covers nothing; there is no z-index or offset argument
          to get wrong, and a bar can count the nodes instead of measuring them.
          `pointer-events-auto` because it now has a control in it. */}
      {panelOpen && (
        <div
          data-testid="agent-controls-detail"
          role="dialog"
          aria-label="AGENT NOTICES"
          className="absolute bottom-full left-0 mb-2 px-3 py-2 rounded-lg text-[9px]
                     font-bold uppercase tracking-wider leading-relaxed text-rexy-muted
                     border border-rexy-border overflow-y-auto"
          style={{ width: 380, maxHeight: 260, background: 'rgba(10,10,26,0.96)', boxShadow: '0 6px 18px rgba(0,0,0,0.5)' }}
        >
          {/* A SECOND WAY OUT, VISIBLE. Escape and click-away are both wired,
              and neither is discoverable by looking at the panel. */}
          <button
            type="button"
            data-testid="notices-close"
            onClick={() => setNoticesOpen(false)}
            aria-label="CLOSE NOTICES"
            className="absolute top-1 right-2 text-[11px] leading-none text-rexy-muted"
            style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}
          >
            ×
          </button>
          {/* ── THE LIVE STRIP (owner ruling 2026-08-29) ──────────────────
              The owner should not need `journalctl` to see his own agent. It
              sits ABOVE the refusals and is visually separated, because it is a
              different KIND of thing: a refusal says what cannot happen, the
              strip says what is happening. Rendering them in one muted stack
              would make the one fact he asked for the hardest to find. */}
          {strip && (
            <div
              data-testid="buck-strip"
              className="pb-1.5 mb-1.5 border-b border-rexy-border"
              style={{
                // Tone carries the state, and the UNREAD tone is amber for the
                // same reason `LOOP ?` is: a surface that could not read must
                // not look like one resting in a known state.
                color: strip.tone === 'walking' ? '#22c55e'
                  : strip.tone === 'armed' ? '#FFD700'
                  : strip.tone === 'unread' ? 'var(--rexy-warning, #f59e0b)'
                  : undefined,
              }}
            >
              {strip.line}
            </div>
          )}
          {/* `data-notice` so the indicator's COUNT can be checked against the
              sentences it claims to be counting, rather than trusted. */}
          {details.map((d, i) => (
            <div key={i} data-notice className={i ? 'mt-1.5' : ''}>{d}</div>
          ))}
        </div>
      )}

      {/* THE RUN CONTROL — the same component the agent's dashboard and the
          domain rail mount, off the same store. It is no longer disabled while
          the state is unread: STOP is offered, because stopping an agent that is
          already off starts nothing and spends nothing, and an owner staring at
          a starved box needs the switch more than he needs the reading. */}
      <RunChip view={runView} onPress={pressRun} testId="run-chip" />

      {/* ── THE NOTICES CHIP (owner ruling 2026-08-31) ────────────────────────
          The persistent indicator the panel now lives behind. It is a chip in
          the row's own idiom, so it costs the row no height — the thing that
          was wrong was never the sentences, it was 380 by 200 pixels of them
          sitting over the conversation with no way to put them away.

          IT RENDERS ONLY WHEN THERE IS SOMETHING TO OPEN. A permanently-present
          ⓘ0 would be the same always-on fault one size smaller.

          AMBER, BECAUSE EVERY SENTENCE IT HOLDS IS A GAP OR A REFUSAL — the
          same amber `LOCAL TTS` unpublished and `STOP` unread already use, for
          the same reason: a surface that could not read must not look like one
          resting in a known state. */}
      {hasPanel && (
        <button
          type="button"
          data-testid="notices-toggle"
          data-count={noticeCount}
          onClick={() => setNoticesOpen((v) => !v)}
          aria-expanded={panelOpen}
          aria-haspopup="dialog"
          title={noticeCount > 0
            ? `${noticeCount} NOTICE${noticeCount === 1 ? '' : 'S'} — CLICK TO READ`
            : 'CLICK FOR THIS AGENT\'S LIVE STATE'}
          // THE SHARED CHIP PADDING, AND THAT IS A MEASURED CHOICE RATHER THAN
          // THE DEFAULT. At 980px WITH THE LOCAL LANE OFFLINE the row has no
          // slack left: this chip squeezes the LOCAL-ONLY group past its wrap
          // point and the row goes 36px -> 42px. I tried to shave it out —
          // px-1.5 took the chip 43px -> 31px, and dropping the count took it to
          // 24px, THE FLOOR — and the row was 42px at every one of them. There
          // is no chip size that fits that state, so the pixels bought nothing
          // and the chip is sized for READING instead. 84 of 85 measured cases
          // are unchanged at 36px; the one exception is named in measure.js with
          // its numbers and flagged to the owner rather than tuned away.
          className={CHIP}
          style={{
            color: 'var(--rexy-warning, #f59e0b)',
            background: panelOpen ? 'rgba(245,158,11,0.16)' : 'transparent',
            boxShadow: panelOpen ? PRESSED : undefined,
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            // EXPLICIT, AND MEASURED RATHER THAN GUESSED. \u24D8 has a taller
            // line box than the row's uppercase text, so the chip's own line
            // height took the whole row from 36px to 42px at 980px — caught by
            // the geometry bar, not by looking at it. The other chips are NOT
            // touched: the literal goes on this chip alone so no shipped byte
            // of the row moves.
            lineHeight: '14px',
          }}
        >
          {noticeCount > 0 ? `\u24D8 ${noticeCount}` : '\u24D8'}
        </button>
      )}

      {/* ── THE TRANSPORT TOGGLE (owner ruling 2026-08-29) ────────────────────
          Two lanes, two ruled labels: LOCAL TTS and PHONE NUMBER. It sits
          IMMEDIATELY LEFT OF CALL because it modifies what CALL does and nothing
          else — adjacency is the only thing that says so without a caption.

          THE CORNERORB IS NOT HERE AND NEVER WILL BE. This control lives in
          ComposerAgentControls, which renders nothing without a {domain, agent}
          pair; the orb calls `ptt()` and reaches no part of this file. The orb's
          exemption is structural rather than a rule someone has to remember.

          TODAY IT IS INERT AND SAYS WHY. Nothing publishes transport capability
          — the route answers 404 while its sibling answers 401 — so the chip
          reads LOCAL TTS in the warning tone with the full sentence in the panel
          above. It arms itself with no edit here the moment the route answers. */}
      <button
        type="button"
        // A STABLE HANDLE, ADDED BECAUSE THE GEOMETRY PROBE PICKED THE WRONG
        // BUTTON WITHOUT ONE. `role="switch"` is not unique in this row — the
        // escalation control in InputBox carries it too — so a probe selecting
        // on the role measured the escalation chip and reported it as this one,
        // including "the transport chip is present" while this chip was hidden.
        data-testid="transport-chip"
        // WHY THE READ FAILED, WHERE THE PERSON FIXING THE ROUTE CAN SEE IT.
        // The owner's sentence says what is true for him and does not change;
        // this says which of the three client paths produced it — a status
        // code, a thrown fetch, or a body that would not parse. Invisible,
        // inspectable in devtools, and assertable by a bar.
        data-transport-unread={transport?.unreadReason || undefined}
        onClick={toggleTransport}
        disabled={transportPending || !tv.armable}
        aria-pressed={tv.onPhone}
        role="switch"
        aria-checked={tv.onPhone}
        // `hint` BEFORE THE GENERIC LINE. It carries a limit that is not worth
        // a counted notice but is owed to whoever reaches for the switch — see
        // TransportVerdict.hint.
        title={tv.detail || tv.hint || `CALLS FROM THIS CHAT GO OUT ON ${tv.label}.`}
        className={CHIP}
        style={{
          // The unpublished state is WARNING, not the armed tone and not a plain
          // grey: a control that cannot do the thing it names must not look the
          // same as one resting in a chosen state. Same amber the loop uses for
          // `LOOP ?`, for the same reason.
          //
          // ── AND THE ARMED PHONE LANE IS NOT GREEN (owner ruling 2026-08-29) ──
          // The first build made it green and that was WRONG, not merely
          // ambiguous. GREEN ALREADY MEANS "STAYS ON THE BOX" in this row —
          // `LOCAL ONLY` sits green two chips to the left. A phone call LEAVES
          // the box, so green on it would assert the opposite of what it does.
          // That is the not-published honesty standard applied to colour.
          //
          // NOR IS THE ANSWER A FIFTH HUE. Blue is escalation-on, gold is the
          // loop, amber is warning and the live call; a new colour in a row that
          // already says LOCAL four times makes it worse. The distinction is
          // TREATMENT: every other chip here is an outline, and this one alone
          // is FILLED — inverted, and still inset-pressed so it keeps the row's
          // depth language. The ↗ is the same sentence in a glyph: it leaves.
          // LITERALS FOR THE ARMED FILL, matching what the loop chip already
          // does for its own armed state (`#FFD700`). Not a style preference: a
          // `var()` with a fallback is DROPPED by jsdom, which left the previous
          // `transparent` in place — so the fill was invisible to the gate while
          // being fine in a browser. A value a bar cannot read is a value the
          // bar is not checking. These are the rexy.dark / rexy.text tokens.
          color: !tv.armable ? 'var(--rexy-warning, #f59e0b)'
            : tv.onPhone ? '#0a0a0a' : undefined,
          background: tv.onPhone ? '#ededed' : 'transparent',
          boxShadow: tv.onPhone ? PRESSED : undefined,
          opacity: transportPending || !tv.armable ? 0.55 : 1,
          cursor: transportPending || !tv.armable ? 'default' : 'pointer',
          whiteSpace: 'nowrap',
        }}
      >
        {transportPending ? 'LANE …' : tv.onPhone ? `\u2197 ${tv.label}` : tv.label}
      </button>

      <button
        type="button"
        onClick={pressCall}
        disabled={!verdict.actionable}
        title={details[details.length - 1] || `CALL ${name}`}
        className={CHIP}
        style={{
          color: verdict.kind === 'in_call' || verdict.kind === 'connecting' ? '#f59e0b' : undefined,
          background: verdict.kind === 'in_call' ? 'rgba(245,158,11,0.16)' : 'transparent',
          boxShadow: verdict.kind === 'in_call' ? PRESSED : undefined,
          opacity: verdict.actionable ? 1 : 0.55,
          cursor: verdict.actionable ? 'pointer' : 'default',
        }}
      >
        {callLabel}
      </button>
    </div>
  );
}
